import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  GuidedFlowDraft,
  GuidedFlowDraftNewCreative,
} from "../components/CreateCampaignModal";
import type { ReviewEditorSection } from "../CreateCampaignReview";

export type GuidedFlowMode =
  | "create"
  | "edit"
  | "edit_new_adset"
  | "edit_new_creative";

export type FlowSectionVisibilityConfig = {
  editor: { audience: boolean; budget: boolean };
  review: { audience: boolean; budget: boolean };
};

export type CreativeTargetAdset = {
  id: string;
  name: string;
  disabled?: boolean;
};

const FLOW_SECTION_VISIBILITY: Record<
  GuidedFlowMode,
  FlowSectionVisibilityConfig
> = {
  create: {
    editor: { audience: true, budget: true },
    review: { audience: true, budget: true },
  },
  edit: {
    editor: { audience: true, budget: false },
    review: { audience: true, budget: false },
  },
  edit_new_adset: {
    editor: { audience: true, budget: false },
    review: { audience: true, budget: false },
  },
  edit_new_creative: {
    editor: { audience: false, budget: false },
    review: { audience: false, budget: false },
  },
};

type UseCreateCampaignFlowStateParams = {
  draft: GuidedFlowDraft;
};

export function useCreateCampaignFlowState({
  draft,
}: UseCreateCampaignFlowStateParams) {
  const flowMode = draft.mode;
  const sectionVisibility = FLOW_SECTION_VISIBILITY[flowMode];
  const isEditMode = flowMode !== "create";
  const showAudienceSection = sectionVisibility.editor.audience;
  const showBudgetSection = sectionVisibility.editor.budget;
  const isNewCreativeMode = flowMode === "edit_new_creative";
  let newCreativeDraft: GuidedFlowDraftNewCreative | null = null;
  if (draft.mode === "edit_new_creative") {
    newCreativeDraft = draft;
  }

  const creativeTargetAdsets: CreativeTargetAdset[] = useMemo(
    () => newCreativeDraft?.creativeTargetAdsets ?? [],
    [newCreativeDraft]
  );
  const selectableCreativeTargetAdsetIds = useMemo(
    () =>
      creativeTargetAdsets
        .filter((adset) => !adset.disabled)
        .map((adset) => adset.id),
    [creativeTargetAdsets]
  );
  const creativeTargetInitKey = useMemo(() => {
    let campaignId = "";
    if (draft.mode !== "create") {
      campaignId = draft.campaignId;
    }
    return `${draft.eventId}:${draft.adAccountId}:${campaignId}`;
  }, [draft]);

  const [adCreativeOpen, setAdCreativeOpen] = useState(true);
  const [audienceOpen, setAudienceOpen] = useState(
    isEditMode && showAudienceSection
  );
  const [budgetOpen, setBudgetOpen] = useState(isEditMode && showBudgetSection);
  const [mobilePanel, setMobilePanel] = useState<"editor" | "preview">(
    "editor"
  );
  const [step, setStep] = useState<"edit" | "review">("edit");
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);
  const [addToAllCreativeAdsets, setAddToAllCreativeAdsets] = useState(false);
  const [selectedCreativeTargetAdsetIds, setSelectedCreativeTargetAdsetIds] =
    useState<string[]>([]);

  const reviewHistoryStateKey = "__kyd_create_campaign_review";

  useEffect(() => {
    if (step !== "review") return;

    if (typeof window !== "undefined") {
      const currentState = window.history.state ?? {};
      if (!currentState?.[reviewHistoryStateKey]) {
        window.history.pushState(
          { ...currentState, [reviewHistoryStateKey]: true },
          ""
        );
      }
    }

    const handlePopState = () => {
      setStep("edit");
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [reviewHistoryStateKey, step]);

  useEffect(() => {
    if (!isNewCreativeMode) return;
    if (selectableCreativeTargetAdsetIds.length > 0) {
      setSelectedCreativeTargetAdsetIds(selectableCreativeTargetAdsetIds);
      setAddToAllCreativeAdsets(true);
      return;
    }

    setSelectedCreativeTargetAdsetIds([]);
    setAddToAllCreativeAdsets(false);
  }, [
    creativeTargetInitKey,
    isNewCreativeMode,
    selectableCreativeTargetAdsetIds,
  ]);

  useEffect(() => {
    if (!isNewCreativeMode || !addToAllCreativeAdsets) return;
    setSelectedCreativeTargetAdsetIds(selectableCreativeTargetAdsetIds);
  }, [
    addToAllCreativeAdsets,
    isNewCreativeMode,
    selectableCreativeTargetAdsetIds,
  ]);

  const hasRequiredCreativeTargetAdsets =
    !isNewCreativeMode || selectedCreativeTargetAdsetIds.length > 0;

  const handleBackToEditor = useCallback(
    (section: ReviewEditorSection = "general") => {
      setStep("edit");
      setMobilePanel("editor");

      if (section === "budget") {
        setBudgetOpen(true);
        if (!isEditMode) {
          setAudienceOpen(false);
        }
      }

      if (section === "audience") {
        setAudienceOpen(true);
        if (!isEditMode) {
          setBudgetOpen(false);
        }
      }

      if (section === "creative") {
        setAdCreativeOpen(true);
      }
    },
    [isEditMode]
  );

  return {
    flowMode,
    sectionVisibility,
    isEditMode,
    showAudienceSection,
    showBudgetSection,
    isNewCreativeMode,
    creativeTargetAdsets,
    selectableCreativeTargetAdsetIds,
    adCreativeOpen,
    setAdCreativeOpen,
    audienceOpen,
    setAudienceOpen,
    budgetOpen,
    setBudgetOpen,
    mobilePanel,
    setMobilePanel,
    step,
    setStep,
    isExitConfirmOpen,
    setIsExitConfirmOpen,
    addToAllCreativeAdsets,
    setAddToAllCreativeAdsets,
    selectedCreativeTargetAdsetIds,
    setSelectedCreativeTargetAdsetIds,
    hasRequiredCreativeTargetAdsets,
    handleBackToEditor,
  };
}
